import { useMemo, useState } from 'react';
import parse from 'html-react-parser';
import {
  ExperienceBookFlowWhatPropertiesModel,
  ExperiencesBookFlowServiceTypePropertiesModel,
  ExperienceBookFlowWhatWhenHowManyWhichElementContentModelContentType,
  ExperienceBookFlowWhatWhenHowManyWhichPropertiesModel,
  ExperienceBookFlowWhatElementModelContentType,
  ExperiencesBookFlowHowManyElementModelContentType,
  ExperienceBookFlowWhenElementModelContentType,
  ExperiencesBookFlowHowManyPropertiesModel,
  ExperienceBookFlowWhenPropertiesModel,
} from '@/services/platform/api/model';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { ProgressDots } from '@/components/shared/progressDots';
import { FormRow } from '@/components/shared/formRow';
import type { BookingState, OpenSheet, ExperienceBookProps } from './types';
import { useClientTranslation } from '@/lib/core/i18n';
import { WhatSheet } from './sheets/what';
import { useIntl } from 'react-intl';
import { formatDate, formatTime, formatUtcDateAndHour, toUtcMidnight } from '@/lib/core/utils';

/* Seven Rooms */

import { HowManySheet as SevenRoomsHowManySheet } from '../eat-and-drink/sheets/howMany';
import { WhenSheet as SevenRoomsWhenSheet } from '../eat-and-drink/sheets/when';

/* Mews */

import { MewsHowManySheet } from '../shared/mews/sheets/howMany';
import { experienceLabels } from '../shared/labels';
import { ExperienceWhenSheet as MewsWhenSheet } from './sheets/when';
import {
  mewsServiceIsSingleOccupancy,
  mewsServiceUsesDateRange,
  mewsServiceUsesHourSlots,
} from '@/services/platform/data/booking/mews';

// ── ExperienceBook ──────────────────────────────────────────────────────────

export function ExperienceBook({
  cmsProps,
  booking,
  onBookingChange,
  onBack,
  onContinue,
  onRequestSuccess,
  context,
  step,
  guestsMax,
  ageConfig,
}: ExperienceBookProps) {
  const t = useClientTranslation();
  const labels = experienceLabels(booking.service?.serviceType);
  const { locale } = useIntl();
  const [openSheet, setOpenSheet] = useState<OpenSheet>(null);

  const bookProps = cmsProps?.what?.items?.[0]?.content?.properties as ExperienceBookFlowWhatPropertiesModel;

  // The same blocks the flow reads its steps from also settle the order of the What list — an
  // editor reordering them there is stating what should come first here too.
  const serviceTypeOrder = useMemo(
    () =>
      (cmsProps?.serviceTypeSteps?.items ?? [])
        .map(
          type => (type?.content?.properties as ExperiencesBookFlowServiceTypePropertiesModel)?.serviceType
        )
        .filter((serviceType): serviceType is string => !!serviceType),
    [cmsProps?.serviceTypeSteps?.items]
  );

  const bookService = cmsProps?.serviceTypeSteps?.items?.find(
    type =>
      (type?.content?.properties as ExperiencesBookFlowServiceTypePropertiesModel)?.serviceType ==
      booking?.service?.serviceType
  )?.content?.properties as ExperiencesBookFlowServiceTypePropertiesModel;

  const bookStep = bookService?.steps?.items?.find(
    s =>
      s?.content?.contentType ==
      ExperienceBookFlowWhatWhenHowManyWhichElementContentModelContentType?.experienceBookFlowWhatWhenHowManyWhich
  )?.content?.properties as ExperienceBookFlowWhatWhenHowManyWhichPropertiesModel;

  const formItems = (bookStep?.steps?.items ?? []).map(
    item => item.content //properties as ExperienceBookFlowFormItemPropertiesModel | undefined
  );

  const titleHtml = bookProps?.title?.markup;
  const continueLabel = t('App.Labels.Continue'); //cmsProps?.continueLabel ?? t('App.Labels.Continue');

  let canContinue =
    booking.venue !== null && booking.guestCount !== null && booking.date !== null && booking.time !== null;

  if (booking?.service?.provider?.toLowerCase() == 'mews') {
    canContinue = !!booking.dateFrom && !!booking.dateTo && booking.adults > 0;
  }

  const venueDisplay = booking?.service?.name ?? booking.venue?.name ?? '';

  // A massage is always for one person and is only offered where it is offered, so both rows are
  // answered the moment the service is picked. They still render — the customer needs to see what
  // was filled in — but greyed out and without the chevron, so nothing invites a tap that would
  // do nothing.
  const isSingleOccupancy = mewsServiceIsSingleOccupancy(booking.service?.serviceType);
  const whereDisplay = booking.venue?.name ?? '';

  /* SevenRooms Values */

  const guestDisplay = booking.guestCount !== null ? `${booking.guestCount}` : '';
  const whenDisplay =
    booking.date && booking.time
      ? `${formatDate(booking.date.toISOString())}, ${formatTime(booking.time)}`
      : '';

  /* Mews Values */

  // Hourly services need an hour on top of the date, and a slot query of their own to offer it.
  const isHourly = mewsServiceUsesHourSlots(ageConfig);
  const enterpriseId = booking.venue?.externalId ?? undefined;
  const serviceId = booking.venue?.bookableServiceExternalId ?? undefined;

  const howManyValue =
    booking.adults > 0 || booking.children > 0
      ? [
          booking.adults > 0 ? `${booking.adults} ${t(labels.adultsCount, { count: booking.adults })}` : '',
          booking.children > 0
            ? `${booking.children} ${t(labels.childrenCount, { count: booking.children })}`
            : '',
        ]
          .filter(Boolean)
          .join(', ')
      : '';
  const isSameUtcDay = (a: Date, b: Date) =>
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate();

  // An hourly service reads as one day plus the hour that was picked.
  const whenValue =
    booking.dateFrom && booking.dateTo
      ? isHourly
        ? formatUtcDateAndHour(booking.dateFrom)
        : isSameUtcDay(booking.dateFrom, booking.dateTo)
          ? formatDate(booking.dateFrom.toISOString())
          : `${formatDate(booking.dateFrom.toISOString())} - ${formatDate(booking.dateTo.toISOString())}`
      : '';

  const rowIsSet = formItems.map(formItem => {
    const stepType = formItem?.contentType;
    switch (stepType) {
      case ExperienceBookFlowWhatElementModelContentType.experienceBookFlowWhat:
        return !!booking.venue;
      case ExperiencesBookFlowHowManyElementModelContentType.experiencesBookFlowHowMany:
        if (booking?.service?.provider?.toLowerCase() == 'sevenrooms') {
          return booking.guestCount !== null;
        } else {
          return booking.adults > 0 || booking.children > 0;
        }
      case ExperienceBookFlowWhenElementModelContentType.experienceBookFlowWhen:
        if (booking?.service?.provider?.toLowerCase() == 'sevenrooms') {
          return !!(booking.date && booking.time);
        } else {
          return !!booking.dateFrom && !!booking.dateTo;
        }
      default:
        return false;
    }
  });

  const isRowDisabled = (index: number) => index > 0 && !rowIsSet.slice(0, index).every(Boolean);

  const handleWhatChange = (updates: Partial<BookingState>) =>
    onBookingChange(
      'venue' in updates
        ? {
            ...updates,
            guestCount: null,
            date: null,
            time: null,
          }
        : updates
    );

  const handleHowManyChange = (updates: Partial<BookingState>) =>
    onBookingChange(
      'guestCount' in updates
        ? {
            ...updates,
            date: null,
            time: null,
          }
        : updates
    );

  return (
    <>
      {/* Header */}
      <header className="flex shrink-0 items-center justify-between px-4 py-2">
        <button
          type="button"
          aria-label="Go back"
          className="flex size-11 cursor-pointer items-center justify-center"
          onClick={onBack}
        >
          <Icon
            name="long-arrow-up-left"
            width={24}
            height={24}
            strokeWidth={2}
            className="text-jncquoi-neutral-1000"
          />
        </button>
        <ProgressDots step={step} />
      </header>

      {/* Scrollable content */}
      <div className="flex flex-1 flex-col gap-8 overflow-y-auto px-4 pt-6 pb-24">
        {titleHtml && <div className="book-intro-title">{parse(titleHtml)}</div>}

        {/* Form rows */}
        <div className="flex flex-col gap-6">
          <FormRow
            key={-1}
            label={bookProps?.stepTitle ?? t('App.Labels.What')}
            value={venueDisplay}
            isEmpty={!booking.venue}
            disabled={isRowDisabled(-1)}
            onClick={() => setOpenSheet('what')}
          />

          {/* Where — filled in by the What selection, never chosen on its own. There is no CMS
              element for it in this flow, so the label comes from the shared translation. */}
          {booking.venue && (
            <FormRow
              key={-2}
              label={t('App.Labels.Where')}
              value={whereDisplay}
              isEmpty={false}
              disabled
              showArrow={false}
              onClick={() => {}}
            />
          )}

          {formItems.map((formItem, index) => {
            const step = formItem;
            const stepType = step?.contentType;
            switch (stepType) {
              case ExperienceBookFlowWhatElementModelContentType.experienceBookFlowWhat:
                return <></>;
              case ExperiencesBookFlowHowManyElementModelContentType.experiencesBookFlowHowMany:
                if (booking?.service?.provider?.toLowerCase() == 'sevenrooms') {
                  return (
                    <FormRow
                      key={index}
                      label={
                        (step?.properties as ExperiencesBookFlowHowManyPropertiesModel).stepTitle ??
                        t('App.Labels.HowMany')
                      }
                      value={guestDisplay}
                      isEmpty={booking.guestCount === null}
                      disabled={isRowDisabled(index)}
                      onClick={() => setOpenSheet('howmany')}
                    />
                  );
                } else {
                  return (
                    <FormRow
                      key={index}
                      label={
                        (step?.properties as ExperiencesBookFlowHowManyPropertiesModel).stepTitle ??
                        t('App.Labels.HowMany')
                      }
                      value={howManyValue}
                      isEmpty={booking.adults === 0 && booking.children === 0}
                      disabled={isSingleOccupancy || isRowDisabled(index)}
                      showArrow={!isSingleOccupancy}
                      onClick={() => {
                        if (isSingleOccupancy) return;
                        setOpenSheet('howmany');
                      }}
                    />
                  );
                }
              case ExperienceBookFlowWhenElementModelContentType.experienceBookFlowWhen:
                if (booking?.service?.provider?.toLowerCase() == 'sevenrooms') {
                  return (
                    <FormRow
                      key={index}
                      label={
                        (step?.properties as ExperienceBookFlowWhenPropertiesModel).stepTitle ??
                        t('App.Labels.When')
                      }
                      value={whenDisplay}
                      isEmpty={!booking.date || !booking.time}
                      disabled={isRowDisabled(index)}
                      onClick={() => setOpenSheet('when')}
                    />
                  );
                } else {
                  return (
                    <FormRow
                      key={index}
                      label={
                        (step?.properties as ExperienceBookFlowWhenPropertiesModel).stepTitle ??
                        t('App.Labels.When')
                      }
                      value={whenValue}
                      isEmpty={!booking.dateFrom || !booking.dateTo}
                      disabled={isRowDisabled(index)}
                      onClick={() => setOpenSheet('when')}
                    />
                  );
                }
              default:
                return null;
            }
          })}
        </div>
      </div>

      {/* Sticky continue button */}
      <div className="fixed bottom-0 w-full shrink-0 border-t-[0.5px] border-[#c8c8c8] bg-white px-4 pt-6 pb-10.5">
        <Button
          variant="primary-solid"
          palette="dark"
          size="lg"
          className="w-full"
          disabled={!canContinue}
          onClick={onContinue}
        >
          {continueLabel}
        </Button>
      </div>

      {/* Sheets */}

      <WhatSheet
        key={-1}
        isOpen={openSheet === 'what'}
        onClose={() => setOpenSheet(null)}
        cmsProps={bookProps as ExperienceBookFlowWhatPropertiesModel}
        booking={booking}
        onBookingChange={handleWhatChange}
        context={context}
        serviceTypeOrder={serviceTypeOrder}
      />

      {formItems.map((formItem, index) => {
        const step = formItem;
        const stepProps = step?.properties;
        const stepType = step?.contentType;
        switch (stepType) {
          case ExperiencesBookFlowHowManyElementModelContentType.experiencesBookFlowHowMany:
            if (booking?.service?.provider?.toLowerCase() == 'sevenrooms') {
              return (
                <SevenRoomsHowManySheet
                  key={index}
                  isOpen={openSheet === 'howmany'}
                  onClose={() => setOpenSheet(null)}
                  cmsProps={stepProps as ExperiencesBookFlowHowManyPropertiesModel}
                  booking={booking}
                  onBookingChange={handleHowManyChange}
                />
              );
            } else {
              return (
                <MewsHowManySheet
                  key={index}
                  labels={experienceLabels(booking.service?.serviceType)}
                  isOpen={openSheet === 'howmany'}
                  onClose={() => setOpenSheet(null)}
                  cmsProps={stepProps as ExperiencesBookFlowHowManyPropertiesModel}
                  ageConfig={ageConfig}
                  guestsMax={guestsMax}
                  numAdults={booking.adults}
                  numChildren={booking.children}
                  onSelect={(adults, children) => {
                    onBookingChange({ adults, children, category: null });
                    setOpenSheet(null);
                  }}
                />
              );
            }
          case ExperienceBookFlowWhenElementModelContentType.experienceBookFlowWhen:
            if (booking?.service?.provider?.toLowerCase() == 'sevenrooms') {
              return (
                <SevenRoomsWhenSheet
                  key={index}
                  isOpen={openSheet === 'when'}
                  onClose={() => setOpenSheet(null)}
                  cmsProps={stepProps as ExperienceBookFlowWhenPropertiesModel}
                  booking={booking}
                  onBookingChange={onBookingChange}
                  onRequestSuccess={onRequestSuccess}
                />
              );
            } else {
              // Either side can turn the range on. The Mews service shape decides by default — a
              // Day or Month service spans several time units — and the CMS flag is an override for
              // steps the configuration does not cover. An hourly service is neither: it picks one
              // day and one hour, so `isHourly` overrules the flag rather than adding to it.
              const isRange =
                !isHourly &&
                (!!(stepProps as ExperienceBookFlowWhenPropertiesModel)?.intervalDatePicker ||
                  mewsServiceUsesDateRange(ageConfig));

              return (
                <MewsWhenSheet
                  key={index}
                  isOpen={openSheet === 'when'}
                  onClose={() => setOpenSheet(null)}
                  cmsProps={stepProps as ExperienceBookFlowWhenPropertiesModel}
                  isRange={isRange}
                  isHourly={isHourly}
                  selectedFrom={booking.dateFrom}
                  selectedTo={booking.dateTo}
                  serviceId={serviceId}
                  enterpriseId={enterpriseId}
                  adults={booking.adults}
                  childrenCount={booking.children}
                  ageConfig={ageConfig}
                  locale={locale}
                  onSelect={({ from, to }) => {
                    onBookingChange({
                      // The hourly sheet already produced the exact instants of the chosen slot —
                      // snapping them to midnight would throw the hour away.
                      dateFrom: isHourly ? from : toUtcMidnight(from),
                      dateTo: isHourly
                        ? to
                        : isRange
                          ? toUtcMidnight(to)
                          : new Date(Date.UTC(to.getFullYear(), to.getMonth(), to.getDate(), 23, 0, 0)),
                      category: null,
                    });
                    setOpenSheet(null);
                  }}
                />
              );
            }
          default:
            return null;
        }
      })}
    </>
  );
}
